import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";

import { insightsFromAIFilters } from "../../config/filters/insightsFromAI";
import { discoveryFilterStore } from "../../store/filters";
import { IHubContentProvider } from "../../types/providers";
import { getFilterOptions } from "../../utils/filters.utils";
import {
    generateInsightFromAIFilterOptions,
    matchesInsightFromAISelections,
} from "../../utils/insightFromAI.utils";
import {
    getAdvertiserId,
    getMarketplaceId,
} from "../../utils/permission.utils";
import { useInsightFamiliesAction } from "../insightFamilies/useInsightFamiliesAction";

export function useInsightsFromAIFiltersLoader(): [AsyncStatus] {
    const [
        setFilterOptions,
        setData,
        fieldSelections,
        contentSubType,
        existingData,
    ] = useStore(discoveryFilterStore, (state) => [
        state.setFilterOptions,
        state.setData,
        state.getInsightsFilters().INSIGHTS_FROM_AI.fieldSelections,
        state.getContentSubType(),
        state.getInsightsFilters().INSIGHTS_FROM_AI.data,
    ]);
    const [insights, setInsights] = useState<InsightFamily[]>(existingData);

    const [insightsActionStatus, insightsAction] = useInsightFamiliesAction({
        advertiserId: getAdvertiserId(),
        marketplaceId: getMarketplaceId(),
    });

    const fetchInsights = useCallback(() => {
        setInsights([]);
        void insightsAction.execute().then((fetchedInsights) => {
            setData(IHubContentProvider.INSIGHTS_FROM_AI, fetchedInsights);
            setInsights(fetchedInsights);
        });
    }, [insightsAction, setFilterOptions, setData, setInsights]);

    useEffect(() => {
        if (isEmpty(existingData)) {
            fetchInsights();
        }
    }, [contentSubType]);

    useEffect(() => {
        insightsFromAIFilters.forEach((filter) => {
            const hasSelections = fieldSelections[filter.field].length > 0;
            const currentFieldSelections = hasSelections
                ? {
                      ...fieldSelections,
                      [filter.field]: [],
                  }
                : fieldSelections;
            const currentMatchedInsights = insights.filter((insight) => {
                return matchesInsightFromAISelections(
                    insight,
                    currentFieldSelections,
                );
            });
            const options = generateInsightFromAIFilterOptions(
                currentMatchedInsights,
            );
            setFilterOptions(
                filter.field,
                getFilterOptions(options[filter.field]),
            );
        });
    }, [insights, fieldSelections]);

    return [insightsActionStatus.status];
}
